import { TailwindColor } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, VariantColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { Option } from '@repo/toolkit/types'

import { diplomasWithPaySlip } from '../../../../components/pages/staff/utils/diplomas-required-documents'
import { getTranslatedName } from '../../../utils/functions/translation.functions'
import {
  AllStaffDocumentTypeEnum,
  DocumentStatusEnum,
  documentStatusEnumMapping
} from '../../enums/common/document-status.enum'
import { StaffPositionEnum } from '../../enums/staff/staff-position.enum'
import {
  frenchLeaderQualifications,
  isStaffEnglish
} from '../../enums/staff/staff-qualifications.enum'
import { TripStepTypeEnum } from '../../enums/trip-bookings/trip-step-type.enum'
import { Contract } from '../contract/contract'
import { CountryLight } from '../country/country'
import { Formula } from '../formula/formula'
import { Option as OptionModal } from '../options/option'
import { PeriodTypes } from '../period/period'
import { Staff } from '../staff/staff'
import { AffectedStaff } from './affected-staff'

export type CenterInformationInput = {
  name: string
  centerToJourneys: {
    journey: {
      reference: string
      mainOption: Pick<OptionModal, 'translations'>
      secondaryOptions: { option: Pick<OptionModal, 'translations'> }[]
      date: {
        period: PeriodTypes.Basic
        departureDate: string | Date
        returnDate: string | Date
        product: { formula: Pick<Formula, 'translations'> }
      }
    }
  }[]
  journeyPlace?: { country: { name: string } }
}

export class AffectedStaffProperties {
  static getVariantFromStatus(status: DocumentStatusEnum | undefined): Option<VariantColor> {
    switch (status) {
      case DocumentStatusEnum.PENDING:
        return { label: 'En attente', value: 'blue' }
      case DocumentStatusEnum.VERIFIED:
        return { label: 'Vérifié', value: 'success' }
      default:
        return { label: 'Reçu', value: 'warning' }
    }
  }

  static getAllStaffDocumentStatus(
    staff: Staff,
    contract?: Contract,
    staffRequestCountry?: CountryLight
  ): Record<AllStaffDocumentTypeEnum, DocumentStatusEnum | 'EMPTY'> {
    const {
      etaStatus,
      criminalRecordStatus,
      idStatus,
      paySlipStatus,
      picture,
      emergencyContactName,
      emergencyContactPhoneNumber,
      diplomas,
      ibanDetailsFilled
    } = staff

    const isPaySlipRequired = this.isPaySlipRequired(staff)
    const filteredDiplomas = diplomas.filter(
      (diploma) => !isPaySlipRequired || !diplomasWithPaySlip.includes(diploma.type)
    )
    const isEtaRequired =
      !isStaffEnglish(staff) && this.isEtaRequiredForCountry(staffRequestCountry)

    const diplomaStatus: DocumentStatusEnum | 'EMPTY' = (() => {
      if (!filteredDiplomas || filteredDiplomas.length === 0) return 'EMPTY'
      const statuses = filteredDiplomas.map((d) => d.status)

      // Anything with REFUSED => REFUSED
      if (statuses.includes(DocumentStatusEnum.REFUSED)) return DocumentStatusEnum.REFUSED

      // All verified => VERIFIED
      if (statuses.every((s) => s === DocumentStatusEnum.VERIFIED))
        return DocumentStatusEnum.VERIFIED

      // All received => RECEIVED
      if (statuses.every((s) => s === DocumentStatusEnum.RECEIVED))
        return DocumentStatusEnum.RECEIVED

      // Declared but not submitted yet should be treated as missing (red)
      if (statuses.every((s) => s === DocumentStatusEnum.PENDING))
        return DocumentStatusEnum.REFUSED

      // Any pending diploma keeps the section in missing state
      if (statuses.includes(DocumentStatusEnum.PENDING)) return DocumentStatusEnum.REFUSED

      // Remaining case: mix of RECEIVED and VERIFIED => RECEIVED
      return DocumentStatusEnum.RECEIVED
    })()

    const ibanStatus: DocumentStatusEnum = ibanDetailsFilled
      ? DocumentStatusEnum.VERIFIED
      : DocumentStatusEnum.REFUSED
    const pictureStatus = picture ? DocumentStatusEnum.VERIFIED : DocumentStatusEnum.REFUSED
    const emergencyContactStatus =
      emergencyContactName && emergencyContactPhoneNumber
        ? DocumentStatusEnum.VERIFIED
        : DocumentStatusEnum.REFUSED
    const signedContractStatus =
      contract?.contractSentAt && !contract?.contractSignedAt
        ? DocumentStatusEnum.PENDING
        : !!contract?.contractSignedAt
          ? DocumentStatusEnum.VERIFIED
          : DocumentStatusEnum.REFUSED

    return {
      [AllStaffDocumentTypeEnum.DIPLOMA]: diplomaStatus,
      [AllStaffDocumentTypeEnum.PAY_SLIP]: isPaySlipRequired
        ? (paySlipStatus ?? DocumentStatusEnum.REFUSED)
        : 'EMPTY',
      [AllStaffDocumentTypeEnum.CRIMINAL_RECORD]:
        criminalRecordStatus ?? DocumentStatusEnum.REFUSED,
      [AllStaffDocumentTypeEnum.ID_FILE]:
        idStatus === DocumentStatusEnum.RECEIVED
          ? DocumentStatusEnum.VERIFIED
          : DocumentStatusEnum.REFUSED,
      [AllStaffDocumentTypeEnum.ETA]: isEtaRequired
        ? (etaStatus ?? DocumentStatusEnum.REFUSED)
        : 'EMPTY',
      [AllStaffDocumentTypeEnum.BANK]: ibanStatus,
      [AllStaffDocumentTypeEnum.PHOTO]: pictureStatus,
      [AllStaffDocumentTypeEnum.EMERGENCY_CONTACT]: emergencyContactStatus,
      [AllStaffDocumentTypeEnum.SIGNED_CONTRACT]: signedContractStatus
    }
  }

  private static isEtaRequiredForCountry(country?: CountryLight): boolean {
    if (!country) return false
    return ['GBEA', 'GBES'].includes(country.code)
  }

  private static isPaySlipRequired(staff: Staff): boolean {
    return staff.position === StaffPositionEnum.TEACHER && !isStaffEnglish(staff)
  }

  private static areAllRequiredDocumentsVerified(
    staff: Staff,
    allStatuses: Record<AllStaffDocumentTypeEnum, DocumentStatusEnum | 'EMPTY'>
  ): boolean {
    const requiresPaySlip = this.isPaySlipRequired(staff)
    const hasNonPaySlipDiploma = staff.diplomas?.some(
      (d) => !requiresPaySlip || !diplomasWithPaySlip.includes(d.type)
    )
    // All non-payslip documents must be VERIFIED
    const nonPaySlipVerified = (Object.keys(allStatuses) as Array<keyof typeof allStatuses>)
      .filter((docType) => {
        if (docType === AllStaffDocumentTypeEnum.PAY_SLIP) return false
        // If there are no non-payslip diplomas, skip DIPLOMA from required checks
        if (!hasNonPaySlipDiploma && docType === AllStaffDocumentTypeEnum.DIPLOMA) return false
        return true
      })
      .every((docType) => allStatuses[docType] === DocumentStatusEnum.VERIFIED)

    // If payslip is required, it must also be VERIFIED; otherwise ignore its status
    const paySlipVerified = !requiresPaySlip
      ? true
      : allStatuses[AllStaffDocumentTypeEnum.PAY_SLIP] === DocumentStatusEnum.VERIFIED
    return nonPaySlipVerified && paySlipVerified
  }

  static checkIfAllDocumentsAreVerified(affectedStaff: AffectedStaff): boolean {
    const allStatuses = this.getAllStaffDocumentStatus(
      affectedStaff.staff,
      affectedStaff.contract,
      affectedStaff.staffRequest?.country
    )
    return this.areAllRequiredDocumentsVerified(affectedStaff.staff, allStatuses)
  }

  static getStatusVariant(status: DocumentStatusEnum | 'EMPTY'): VariantColor | undefined {
    if (status === 'EMPTY') return 'grey'
    return documentStatusEnumMapping[status].variant
  }

  static getStatusColor(status: DocumentStatusEnum | 'EMPTY'): TailwindColor | undefined {
    const variant = this.getStatusVariant(status)
    if (variant) return colorsMapping[variant].text
  }

  static getMainOptions(
    centers: {
      centerToJourneys: { journey: { mainOption: Pick<OptionModal, 'translations'> } }[]
    }[],
    currentLanguage: AvailableLanguages
  ): string[] {
    return centers.flatMap((c) =>
      c.centerToJourneys.map((centerToJourney) => {
        const journey = centerToJourney.journey
        return getTranslatedName(journey.mainOption, currentLanguage) || '—'
      })
    )
  }

  static getSecondaryOptions(
    centers: {
      centerToJourneys: {
        journey: { secondaryOptions: { option: Pick<OptionModal, 'translations'> }[] }
      }[]
    }[],
    currentLanguage: AvailableLanguages
  ): string[] {
    return centers.flatMap((c) =>
      c.centerToJourneys.flatMap((centerToJourney) => {
        const journey = centerToJourney.journey
        return journey.secondaryOptions?.map(
          (secondaryOption) => getTranslatedName(secondaryOption.option, currentLanguage) || '—'
        )
      })
    )
  }

  static getOptions(
    centers: {
      centerToJourneys: {
        journey: {
          mainOption: Pick<OptionModal, 'translations'>
          secondaryOptions: { option: Pick<OptionModal, 'translations'> }[]
        }
      }[]
    }[],
    currentLanguage: AvailableLanguages
  ): string {
    const mainOptions = this.getMainOptions(centers, currentLanguage)
    const secondaryOptions = this.getSecondaryOptions(centers, currentLanguage)
    const uniqueOptions = Array.from(new Set([...mainOptions, ...secondaryOptions]))
    return uniqueOptions.join(' , ')
  }

  /**
   *  Get the center data
   * @param center  - The center object containing journey data
   * @returns   An object with period
   */
  static getCenterInformation(center: CenterInformationInput): {
    details: string[]
    period: PeriodTypes.Basic
    country: string | undefined
  }[] {
    return center.centerToJourneys.map((centerToJourney) => {
      const journey = centerToJourney.journey
      const date = `${DateFormatter.DDMMYYYY(journey.date.departureDate)} à ${DateFormatter.DDMMYYYY(journey.date.returnDate)}`

      const durationInDays = DateFormatter.getDurationInDays(
        journey.date.departureDate,
        journey.date.returnDate
      )

      const duration = `${durationInDays} jours`
      const secondaryOptions = journey.secondaryOptions
        .map((secondaryOption) => getTranslatedName(secondaryOption.option))
        .join(' , ')
      const mainOption = getTranslatedName(journey.mainOption)
      const formula = getTranslatedName(journey.date.product.formula)
      const centerName = center.name
      const country = center.journeyPlace?.country.name

      const details: string[] = []
      details.push(centerName)
      details.push(formula)
      details.push(mainOption)
      if (secondaryOptions) details.push(`${mainOption} , ${secondaryOptions}`)
      details.push(date)
      details.push(duration)

      return { details, period: journey.date.period, country }
    })
  }

  /**
   *  Get the affected staff journeys for a specific center
   * @param center - The center object containing journey data
   * @returns   An array of objects containing reference and period for each affected staff journey
   */
  static getAffectedStaffJourneys(center: CenterInformationInput[]): {
    reference: string
    period: PeriodTypes.Basic
  }[] {
    return center.flatMap((c) =>
      c.centerToJourneys.map((centerToJourney) => {
        const journey = centerToJourney.journey
        return { reference: journey.reference, period: journey.date.period }
      })
    )
  }

  static getStaffJourneyDuration(durationInDays?: number): {
    days: number
    nights: number
  } {
    if (!durationInDays)
      return {
        days: 0,
        nights: 0
      }

    return {
      days: durationInDays,
      nights: durationInDays - 1
    }
  }

  static getTripBookingDispatchPlaces(affectedStaff: AffectedStaff) {
    const allerTrip = affectedStaff.tripBookingDispatchPlaces.find(
      (trip) => trip.directionality === TripStepTypeEnum.INBOUND
    )

    const retourTrip = affectedStaff.tripBookingDispatchPlaces.find(
      (trip) => trip.directionality === TripStepTypeEnum.OUTBOUND
    )

    return { allerTrip, retourTrip }
  }

  static isFrench(affectedStaff: AffectedStaff | null): boolean {
    const staffQualification = affectedStaff?.staffRequest.qualification
    if (!staffQualification) return false
    return frenchLeaderQualifications.includes(staffQualification)
  }
}
